import { CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class representing a Slideshow component.
 */
export default class Slideshow extends MessageBus {
  /**
   * Constructor initializes the slideshow.
   * @param {HTMLElement} element - The container element for the slideshow.
   */
  constructor(element) {
    super();
    this.element = element;
    this.totalSlides =
      parseInt(
        this.element.getAttribute(`${SELECTORS.DATA_SLIDESHOW_TOTAL}`),
        10
      ) || 0;
    this.currentSlideIndex =
      parseInt(
        this.element.getAttribute(`${SELECTORS.DATA_SLIDESHOW_CURRENT}`),
        10
      ) || 0;

    this.init();
  }

  /**
   * Initializes the slideshow by caching DOM elements,
   * setting up event handlers, and enabling functionality.
   * @returns {Slideshow} - Returns the current instance.
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Caches all necessary DOM elements used in the slideshow.
   * @returns {Slideshow} - Returns the current instance.
   */
  cacheDomReferences() {
    this.prevButton = this.element.querySelector(
      `.${CLASS_NAMES.SLIDESHOW_COUNTER_PREV}`
    );
    this.nextButton = this.element.querySelector(
      `.${CLASS_NAMES.SLIDESHOW_COUNTER_NEXT}`
    );
    this.counterCurrent = this.element.querySelector(
      `.${CLASS_NAMES.SLIDESHOW_COUNTER_CURRENT}`
    );
    this.slides = this.element.querySelectorAll(
      `.${CLASS_NAMES.SLIDESHOW_SLIDE_IMAGE}`
    );
    this.captionHeadline = this.element.querySelector(
      `.${CLASS_NAMES.SLIDESHOW_SLIDE_HEADLINE}`
    );
    this.captionDescription = this.element.querySelector(
      `.${CLASS_NAMES.SLIDESHOW_SLIDE_DESCRIPTION}`
    );
    this.dataElements = this.element.querySelectorAll(
      `.${CLASS_NAMES.SLIDESHOW_DATA} ${SELECTORS.SPAN}`
    );
    return this;
  }

  /**
   * Sets up event handlers with proper binding.
   * @returns {Slideshow} - Returns the current instance.
   */
  setupHandlers() {
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    return this;
  }

  /**
   * Enables event listeners for navigation buttons.
   * @returns {Slideshow} - Returns the current instance.
   */
  enable() {
    if (this.prevButton)
      this.prevButton.addEventListener(EVENTS.CLICK, this.handlePrevClick);
    if (this.nextButton)
      this.nextButton.addEventListener(EVENTS.CLICK, this.handleNextClick);
    return this;
  }

  /**
   * Handles the previous slide button click event.
   * @param {Event} event - The event object.
   */
  handlePrevClick(event) {
    event.preventDefault();
    let newSlideIndex = this.currentSlideIndex - 1;
    if (newSlideIndex < 0) newSlideIndex = this.totalSlides - 1;
    this.displayNewSlide(newSlideIndex);
  }

  /**
   * Handles the next slide button click event.
   * @param {Event} event - The event object.
   */
  handleNextClick(event) {
    event.preventDefault();
    let newSlideIndex = this.currentSlideIndex + 1;
    if (newSlideIndex >= this.totalSlides) newSlideIndex = 0;
    this.displayNewSlide(newSlideIndex);
  }

  /**
   * Displays a new slide by updating the active class,
   * caption headline, and description.
   * @param {number} index - The index of the new slide.
   */
  displayNewSlide(index) {
    if (this.slides[this.currentSlideIndex]) {
      this.slides[this.currentSlideIndex].classList.remove(
        `${CLASS_NAMES.ACTIVE}`
      );
    }
    if (this.slides[index]) {
      this.slides[index].classList.add(`${CLASS_NAMES.ACTIVE}`);
    }

    const dataElement = [...this.dataElements].find(
      (el) =>
        el.getAttribute(`${SELECTORS.DATA_SLIDESHOW_SLIDE}`) === String(index)
    );
    if (dataElement) {
      this.captionHeadline.textContent = dataElement.getAttribute(
        `${SELECTORS.DATA_SLIDESHOW_HEADLINE}`
      );
      this.captionDescription.textContent = dataElement.getAttribute(
        `${SELECTORS.DATA_SLIDESHOW_DESCRIPTION}`
      );
    }

    this.currentSlideIndex = index;
    this.element.setAttribute(`${SELECTORS.DATA_SLIDESHOW_CURRENT}`, index);
    if (this.counterCurrent) this.counterCurrent.textContent = index + 1;
  }
}
